import { useCallback, useRef, useState } from "react";
import { visibleHeaderOffset } from "@/lib/useHeaderHeightVar";

/** Mimics `position: sticky` using a sentinel + IntersectionObserver, then
 * switches to real `position: fixed` only once actually stuck. Avoids two
 * problems at once: (1) iOS Safari's long-standing WebKit bug with two
 * stacked/nested `sticky` elements (this bar + the header above), and
 * (2) the overlap you get from making a bar `fixed` unconditionally —
 * content that used to sit above it in normal flow would otherwise render
 * underneath the fixed bar instead of pushing it down. Render a 1px
 * sentinel <div> exactly where the bar naturally sits, right before it.
 * Uses a callback ref (not useRef+useEffect) because the sentinel is
 * often conditionally rendered after data loads — a plain useEffect with
 * an empty deps array would run once against a null ref and never
 * re-attach once the sentinel actually appears. */
export function useStickyBar(offsetVar = "--sticky-header-h") {
  const [isStuck, setIsStuck] = useState(false);
  const cleanupRef = useRef(null);

  const sentinelRef = useCallback((el) => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (!el) return;
    let observer;
    let frame = null;
    let headerPx = 0;
    const check = () => {
      frame = null;
      // A header may have scrolled offscreen or changed height since setup.
      // Re-read its visible offset instead of retaining an empty header gap.
      headerPx = visibleHeaderOffset(offsetVar);
      setIsStuck(el.getBoundingClientRect().top <= headerPx);
    };
    const scheduleCheck = () => {
      if (frame === null) frame = requestAnimationFrame(check);
    };
    const setup = () => {
      if (observer) observer.disconnect();
      headerPx = visibleHeaderOffset(offsetVar);
      observer = new IntersectionObserver(
        scheduleCheck,
        { rootMargin: `-${headerPx}px 0px 0px 0px`, threshold: 0 }
      );
      observer.observe(el);
      scheduleCheck();
    };
    setup();
    // iOS standalone PWAs can stop delivering IntersectionObserver updates
    // during long touch scrolls. The passive scroll fallback keeps the fixed
    // state synchronized without doing more than one measurement per frame.
    window.addEventListener("scroll", scheduleCheck, { passive: true });
    window.addEventListener("resize", setup);
    window.visualViewport?.addEventListener("resize", setup);
    cleanupRef.current = () => {
      observer?.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", scheduleCheck);
      window.removeEventListener("resize", setup);
      window.visualViewport?.removeEventListener("resize", setup);
    };
  }, [offsetVar]);

  return [sentinelRef, isStuck];
}
